import { useState, useEffect, useCallback, CSSProperties } from 'react';
import {
  databaseService, TestRun, TestRunPcStat, TestRunSceneStat, TestRunSoakStat, TestRunSoakCrash,
} from '../services/database';
import { createPCAgentClient } from '../services/pcAgent';
import { SoakRunner, getActiveSoakRunner, setActiveSoakRunner } from '../services/soakRunner';
import { playAlertTone } from '../utils/alertTone';
import { Operator, PCInstance } from '../types/database';

interface TestLabViewProps {
  roomId?: string;
  operator: Operator | null;
}

interface LaunchResult {
  pcName: string;
  ok: boolean;
  message: string;
}

// A single PC's candidate hosts, LAN first then Tailscale (matches the rest of the app).
function pcHosts(pc: PCInstance): (string | undefined | null)[] {
  return [pc.ip_address, pc.tailscale_ip];
}

export default function TestLabView({ roomId, operator }: TestLabViewProps) {
  const [pcs, setPcs] = useState<PCInstance[]>([]);
  const [experiences, setExperiences] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // 'stream' = the original labeled streaming test (launch once, record the
  // telemetry window). 'soak' = automated build soak: walk the scenes with
  // dwell time, complete at the end, repeat, watching for crashes.
  const [mode, setMode] = useState<'stream' | 'soak'>('stream');

  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [experienceId, setExperienceId] = useState('');
  const [version, setVersion] = useState('');
  const [language, setLanguage] = useState('');
  // Auto tests can't clear scene 0 (it waits for a player to touch-to-start), so
  // launches start at scene 1+ via the crash-resume -scene_index arg.
  const [sceneMode, setSceneMode] = useState<'full' | 'single'>('full');
  const [sceneNumber, setSceneNumber] = useState(1);

  // Soak-specific knobs.
  const [soakVersions, setSoakVersions] = useState('');
  const [dwellSec, setDwellSec] = useState(30);
  const [finalSoakSec, setFinalSoakSec] = useState(60);
  const [cyclesPerPc, setCyclesPerPc] = useState(0);

  const [activeRun, setActiveRun] = useState<TestRun | null>(null);
  const [recent, setRecent] = useState<TestRun[]>([]);
  const [results, setResults] = useState<LaunchResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [killOnStop, setKillOnStop] = useState(true);

  // Live soak engine. Held at module level too, so navigating away from the
  // page and back re-attaches to the running soak.
  const [runner, setRunner] = useState<SoakRunner | null>(() => getActiveSoakRunner());
  const [, setSoakTick] = useState(0);

  // Confirmation modal state; busyPcs = PCs currently running an experience (possible customers).
  const [confirming, setConfirming] = useState<{ busyPcs: string[] } | null>(null);

  // Report / visualization state for a selected run.
  const [selectedRun, setSelectedRun] = useState<TestRun | null>(null);
  const [pcStats, setPcStats] = useState<TestRunPcStat[]>([]);
  const [sceneStats, setSceneStats] = useState<TestRunSceneStat[]>([]);
  const [soakStats, setSoakStats] = useState<TestRunSoakStat[]>([]);
  const [soakCrashes, setSoakCrashes] = useState<TestRunSoakCrash[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportView, setReportView] = useState<'pc' | 'scene'>('pc');
  const [metricKey, setMetricKey] = useState<'net_sent_avg' | 'bitrate_avg' | 'render_fps_avg' | 'transmit_avg'>('net_sent_avg');

  const load = useCallback(async () => {
    if (!roomId) {
      setPcs([]);
      return;
    }
    const [pcList, expList, active, recentRuns] = await Promise.all([
      databaseService.getPCInstancesInRoom(roomId),
      databaseService.listExperiences(),
      databaseService.getActiveTestRun(roomId),
      databaseService.listRecentTestRuns(roomId, 15),
    ]);
    setPcs(pcList);
    setExperiences(expList);
    setActiveRun(active);
    setRecent(recentRuns);
    // Default selection: all online PCs.
    setSelected(new Set(pcList.filter((p) => databaseService.isPCOnline(p.last_heartbeat)).map((p) => p.id)));
  }, [roomId]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-render on every soak engine update while a runner is attached.
  useEffect(() => {
    if (!runner) return;
    return runner.subscribe(() => setSoakTick((t) => t + 1));
  }, [runner]);

  // Repeating crash/offline alarm: rings while there are unacknowledged alerts.
  // Self-contained (the Dashboard's tile alarm is unmounted on this page).
  const unackedAlerts = runner?.unackedAlerts ?? 0;
  const alarmActive = unackedAlerts > 0;
  useEffect(() => {
    if (!alarmActive) return;
    playAlertTone();
    const id = setInterval(() => playAlertTone(), 10_000);
    return () => clearInterval(id);
  }, [alarmActive]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedPcs = pcs.filter((p) => selected.has(p.id));
  const startScene = sceneMode === 'full' ? 1 : Math.max(0, sceneNumber || 0);
  const sceneLabel = sceneMode === 'full' ? 'full flow (from scene 1)' : `scene ${startScene} only`;
  const soakVersionList = soakVersions.split(',').map((v) => v.trim()).filter(Boolean);

  // Step 1 of Start: validate, then probe EVERY PC in the room for a running
  // experience. Any hit is a hard block — a running experience means the room
  // may be in use, and neither test mode is allowed to interrupt that. (The
  // operator ends/kills the session first if the run really is stale.)
  const beginStart = async () => {
    if (!label.trim()) { alert('Give the test run a label first.'); return; }
    if (!experienceId) { alert('Pick an experience to launch.'); return; }
    if (selectedPcs.length === 0) { alert('Select at least one PC.'); return; }

    setBusy(true);
    try {
      const statuses = await Promise.all(
        pcs.map(async (pc) => {
          const client = createPCAgentClient(pcHosts(pc));
          const status = await client.getStatus();
          return { pc, exp: status?.current_experience };
        })
      );
      const busyPcs = statuses
        .filter((s) => s.exp)
        .map((s) => `${s.pc.pc_name || s.pc.id} (${s.exp!.name}${s.exp!.is_server ? ', server' : ''})`);
      setConfirming({ busyPcs });
    } finally {
      setBusy(false);
    }
  };

  // Step 2 of Start (stream mode): write the logbook row and launch on the selected PCs.
  const doStart = async () => {
    setConfirming(null);
    setBusy(true);
    setResults([]);
    try {
      const run = await databaseService.createTestRun({
        label: label.trim(),
        configNotes: [notes.trim(), sceneLabel].filter(Boolean).join(' · ') || undefined,
        roomId,
        pcNames: selectedPcs.map((p) => p.pc_name || p.id),
        experienceId,
        experienceVersion: version.trim() || undefined,
        startedBy: operator?.name || operator?.id,
      });
      if (!run) {
        alert('Could not create the test-run record. Nothing was launched.');
        return;
      }

      const launchResults = await Promise.all(
        selectedPcs.map(async (pc): Promise<LaunchResult> => {
          const client = createPCAgentClient(pcHosts(pc));
          const res = await client.launchExperience({
            experience_id: experienceId,
            version: version.trim() || undefined,
            operator_id: operator?.id,
            language: language.trim() || undefined,
            scene_index: startScene,
          });
          return {
            pcName: pc.pc_name || pc.id,
            ok: res.success,
            message: res.success ? (res.message || 'Launched') : (res.error || 'Failed'),
          };
        })
      );

      setResults(launchResults);
      setActiveRun(run);
      setRecent(await databaseService.listRecentTestRuns(roomId, 15));
    } finally {
      setBusy(false);
    }
  };

  // Step 2 of Start (soak mode): write the logbook row and hand the PCs to the
  // soak engine. Crash evidence lands in the DB via the agents themselves
  // (no_auto_restart launches); this page keeps the live view + alarm.
  const doStartSoak = async () => {
    setConfirming(null);
    setBusy(true);
    setResults([]);
    try {
      const versions = soakVersionList.length > 0 ? soakVersionList : [''];
      const run = await databaseService.createTestRun({
        label: label.trim(),
        configNotes: [
          `SOAK · dwell ${dwellSec}s · final ${finalSoakSec}s · ${cyclesPerPc === 0 ? 'until stopped' : `${cyclesPerPc}×/PC`}`,
          notes.trim(),
        ].filter(Boolean).join(' · '),
        roomId,
        pcNames: selectedPcs.map((p) => p.pc_name || p.id),
        experienceId,
        experienceVersion: versions.filter(Boolean).join(' / ') || undefined,
        startedBy: operator?.name || operator?.id,
      });
      if (!run) {
        alert('Could not create the test-run record. Nothing was launched.');
        return;
      }

      const r = new SoakRunner({
        pcs: selectedPcs.map((p) => ({ id: p.id, name: p.pc_name || p.id, hosts: pcHosts(p) })),
        experienceId,
        versions,
        language: language.trim() || undefined,
        startScene: Math.max(1, startScene), // scene 0 never clears unattended
        dwellSec: Math.max(0, dwellSec),
        finalSoakSec: Math.max(0, finalSoakSec),
        maxSteps: 12,
        cyclesPerPc: Math.max(0, cyclesPerPc),
        operatorId: operator?.id,
      });
      setActiveSoakRunner(r);
      setRunner(r);
      r.start();
      setActiveRun(run);
      setRecent(await databaseService.listRecentTestRuns(roomId, 15));
    } finally {
      setBusy(false);
    }
  };

  const doStop = async () => {
    if (!activeRun) return;
    setBusy(true);
    try {
      await databaseService.endTestRun(activeRun.id);

      if (killOnStop) {
        // Kill on the PCs that were part of this run (match by name).
        const runPcs = pcs.filter((p) => activeRun.pc_names.includes(p.pc_name || p.id));
        await Promise.all(
          runPcs.map(async (pc) => {
            const client = createPCAgentClient(pcHosts(pc));
            try { await client.killExperience(false, operator?.id); } catch { /* best effort */ }
          })
        );
      }

      setActiveRun(null);
      setResults([]);
      setRecent(await databaseService.listRecentTestRuns(roomId, 15));
      setSelectedRun(null); // triggers the effect to auto-open the just-ended run's report
    } finally {
      setBusy(false);
    }
  };

  // Stop the soak: wind the loops down (each kills its experience), close the
  // logbook row, open the report.
  const stopSoak = async () => {
    if (!runner) return;
    setBusy(true);
    try {
      await runner.stop();
      setActiveSoakRunner(null);
      setRunner(null);
      if (activeRun) await databaseService.endTestRun(activeRun.id);
      setActiveRun(null);
      setRecent(await databaseService.listRecentTestRuns(roomId, 15));
      setSelectedRun(null);
    } finally {
      setBusy(false);
    }
  };

  // Load a run's per-PC + per-scene aggregates for the visualization.
  const openReport = useCallback(async (run: TestRun) => {
    setSelectedRun(run);
    setReportLoading(true);
    try {
      const [pc, scene, soak, crashes] = await Promise.all([
        databaseService.getTestRunReport(run.id),
        databaseService.getTestRunSceneReport(run.id),
        databaseService.getTestRunSoakReport(run.id),
        databaseService.getTestRunSoakCrashes(run.id),
      ]);
      setPcStats(pc);
      setSceneStats(scene);
      setSoakStats(soak);
      setSoakCrashes(crashes);
    } finally {
      setReportLoading(false);
    }
  }, []);

  // Auto-open the most recent run's report once the list is loaded / after a run ends.
  useEffect(() => {
    if (!selectedRun && recent.length > 0) {
      openReport(recent[0]);
    }
  }, [recent, selectedRun, openReport]);

  const fmtTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDur = (r: TestRun) => {
    const end = r.ended_at ? new Date(r.ended_at) : new Date();
    const mins = Math.max(0, Math.round((end.getTime() - new Date(r.started_at).getTime()) / 60000));
    return `${mins} min${r.ended_at ? '' : ' (running)'}`;
  };

  if (!roomId) {
    return <div style={{ padding: 24 }}>Select a room to run streaming tests.</div>;
  }

  const card: CSSProperties = {
    background: '#fff', border: '1px solid #e4e9f0', borderRadius: 12, padding: 20, marginBottom: 16,
  };
  const labelStyle: CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 600, color: '#54626f', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
  };
  const input: CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid #d5dce5', borderRadius: 8, fontSize: 14, marginBottom: 12,
  };
  const thTd = (head = false, left = false): CSSProperties => ({
    textAlign: left ? 'left' : 'right', padding: '6px 10px', borderBottom: '1px solid #eef1f5',
    whiteSpace: 'nowrap', fontVariant: 'tabular-nums', color: head ? '#8a97a6' : '#16202e',
    fontWeight: head ? 600 : 400, fontSize: head ? 11 : 12, textTransform: head ? 'uppercase' : 'none',
  });

  const METRICS: { key: typeof metricKey; label: string; unit: string }[] = [
    { key: 'net_sent_avg', label: 'Send', unit: 'Mbps' },
    { key: 'bitrate_avg', label: 'Bitrate', unit: 'Mbps' },
    { key: 'render_fps_avg', label: 'Render FPS', unit: 'fps' },
    { key: 'transmit_avg', label: 'Transmit', unit: 'ms' },
  ];
  const wavg = (rows: TestRunPcStat[], field: keyof TestRunPcStat) => {
    let num = 0, den = 0;
    for (const r of rows) {
      const v = r[field] as number | null;
      if (v != null) { num += v * r.samples; den += r.samples; }
    }
    return den ? num / den : null;
  };
  const fmt = (v: number | null | undefined, d = 0) => (v == null ? '—' : v.toFixed(d));

  // Live panel for a running (or just-finished, not yet saved) soak.
  const renderActiveSoak = () => {
    if (!runner) return null;
    const tallies = Array.from(runner.tallies.values());
    const pcStates = Array.from(runner.pcStates.values());
    const finished = !runner.running;
    return (
      <div style={{ ...card, borderColor: finished ? '#8CB8EA' : '#E7B75F', background: finished ? '#F3F8FE' : '#FEFAF1' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: finished ? '#2B6AC0' : '#8a5a10', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {finished ? 'Soak finished — save it to close the run' : 'Soak running'}
        </div>
        <div style={{ fontSize: 20, fontWeight: 680, margin: '4px 0' }}>{activeRun?.label || label || 'Soak test'}</div>
        <div style={{ fontSize: 13, color: '#54626f' }}>
          {pcStates.length} PC(s){activeRun ? ` · started ${fmtTime(activeRun.started_at)} · ${fmtDur(activeRun)}` : ''} · keep this window open — the walk runs from here.
        </div>

        {unackedAlerts > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#FDECEA', border: '1px solid #f5b5ae', color: '#8a1c12', borderRadius: 8, padding: '10px 12px', fontSize: 14, margin: '12px 0' }}>
            <span style={{ fontSize: 18 }}>🚨</span>
            <span style={{ flex: 1 }}><strong>{unackedAlerts} crash/offline alert(s)</strong> — see the event log below.</span>
            <button className="refresh-btn" onClick={() => runner.acknowledgeAlerts()}>Acknowledge (mute)</button>
          </div>
        )}

        <div style={{ overflowX: 'auto', margin: '14px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['Version', 'Cycles', 'Completed', 'Crashes', 'Crash %', 'Inconcl.', 'Offline', 'Launch fails', 'Crash scenes'].map((h, i) => (
              <th key={h} style={thTd(true, i === 0 || i === 8)}>{h}</th>))}</tr></thead>
            <tbody>
              {tallies.map((t) => (
                <tr key={t.version || 'latest'}>
                  <td style={thTd(false, true)}>{t.version || 'latest'}</td>
                  <td style={thTd()}>{t.cycles}</td>
                  <td style={thTd()}>{t.completed}</td>
                  <td style={{ ...thTd(), color: t.crashes > 0 ? '#b3261e' : undefined, fontWeight: t.crashes > 0 ? 700 : 400 }}>{t.crashes}</td>
                  <td style={thTd()}>{t.cycles ? ((100 * t.crashes) / t.cycles).toFixed(1) + '%' : '—'}</td>
                  <td style={thTd()}>{t.inconclusive}</td>
                  <td style={thTd()}>{t.offline}</td>
                  <td style={thTd()}>{t.launchFails}</td>
                  <td style={{ ...thTd(false, true), whiteSpace: 'normal' }}>
                    {Object.entries(t.byScene).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${k} ×${n}`).join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 8, marginBottom: 14 }}>
          {pcStates.map((s) => (
            <div key={s.pcName} style={{ border: '1px solid #e4e9f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{s.pcName}</strong>
                <span style={{ color: '#8a97a6', fontVariant: 'tabular-nums' }}>#{s.cycle}{s.version ? ` · ${s.version}` : ''}</span>
              </div>
              <div style={{ color: s.done ? '#8a97a6' : '#16202e', marginTop: 2 }}>{s.phase}</div>
            </div>
          ))}
        </div>

        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eef1f5', borderRadius: 8, padding: '6px 10px', background: '#fff', fontFamily: 'ui-monospace, monospace', fontSize: 12, marginBottom: 14 }}>
          {runner.events.length === 0 && <div style={{ color: '#8a97a6' }}>No events yet.</div>}
          {runner.events.slice(0, 60).map((e, i) => (
            <div key={`${e.ts}-${i}`} style={{ color: e.severity === 'error' ? '#b3261e' : e.severity === 'warn' ? '#8a5a10' : '#54626f', padding: '1px 0' }}>
              {new Date(e.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}{' '}
              <strong>{e.pcName}</strong> {e.kind} — {e.detail}
            </div>
          ))}
        </div>

        <button className="refresh-btn" disabled={busy} onClick={stopSoak}
          style={{ background: finished ? '#2B6AC0' : '#b3261e', color: '#fff', fontWeight: 600 }}>
          {busy ? 'Stopping…' : finished ? 'Save & close soak run' : 'Stop soak'}
        </button>
      </div>
    );
  };

  const renderReport = () => {
    const run = selectedRun!;
    const barRows: { name: string; value: number | null }[] =
      reportView === 'pc'
        ? pcStats.map((s) => ({ name: s.pc_name, value: (s as any)[metricKey] }))
        : sceneStats.map((s) => ({ name: s.scene_name || '(no scene)', value: (s as any)[metricKey] }));
    const maxVal = Math.max(1, ...barRows.map((r) => r.value ?? 0));
    const totalSamples = pcStats.reduce((a, s) => a + s.samples, 0);

    return (
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 680 }}>{run.label}</div>
            <div style={{ fontSize: 13, color: '#54626f' }}>
              {run.config_notes ? `${run.config_notes} · ` : ''}{fmtTime(run.started_at)}–{fmtTime(run.ended_at)} · {fmtDur(run)}
            </div>
          </div>
          <button className="refresh-btn" onClick={() => openReport(run)}>Refresh</button>
        </div>

        {reportLoading ? (
          <div style={{ padding: '18px 0', color: '#8a97a6' }}>Loading results…</div>
        ) : (
          <>
            {soakStats.length > 0 && (
              <>
                <div style={{ ...labelStyle, marginTop: 16 }}>Soak verdicts (from the agents' run + crash records)</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
                    <thead><tr>{['Version', 'Cycles', 'Completed', 'Crashed', 'Crash %', 'Avg run (min)', 'Crash scenes'].map((h, i) => (
                      <th key={h} style={thTd(true, i === 0 || i === 6)}>{h}</th>))}</tr></thead>
                    <tbody>
                      {soakStats.map((s) => (
                        <tr key={s.version}>
                          <td style={thTd(false, true)}>{s.version}</td>
                          <td style={thTd()}>{s.runs}</td>
                          <td style={thTd()}>{s.completed}</td>
                          <td style={{ ...thTd(), color: s.crashed > 0 ? '#b3261e' : undefined, fontWeight: s.crashed > 0 ? 700 : 400 }}>{s.crashed}</td>
                          <td style={thTd()}>{s.runs ? ((100 * s.crashed) / s.runs).toFixed(1) + '%' : '—'}</td>
                          <td style={thTd()}>{fmt(s.avg_run_min, 1)}</td>
                          <td style={{ ...thTd(false, true), whiteSpace: 'normal' }}>{s.crash_scenes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {soakCrashes.length > 0 && (
                  <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead><tr>{['Crash at', 'PC', 'Version', 'Scene', 'Exit code'].map((h, i) => (
                        <th key={h} style={thTd(true, i < 4)}>{h}</th>))}</tr></thead>
                      <tbody>
                        {soakCrashes.map((c, i) => (
                          <tr key={`${c.crashed_at}-${i}`}>
                            <td style={thTd(false, true)}>{new Date(c.crashed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                            <td style={thTd(false, true)}>{c.pc_name}</td>
                            <td style={thTd(false, true)}>{c.version || '—'}</td>
                            <td style={thTd(false, true)}>{c.scene_name || `L${c.scene_index}`}</td>
                            <td style={thTd()}>{c.exit_code}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {pcStats.length === 0 ? (
              <div style={{ padding: '14px 0', color: '#8a97a6' }}>
                No telemetry recorded in this run's window — likely too short, or the PCs hadn't started posting yet
                (experiences take ~30–60s after launch to begin reporting). Try a run of a few minutes.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 10, margin: '16px 0' }}>
                  {[
                    { k: 'PCs', v: `${pcStats.length}` },
                    { k: 'Samples', v: `${totalSamples}` },
                    { k: 'Send Mbps', v: fmt(wavg(pcStats, 'net_sent_avg')) },
                    { k: 'Bitrate Mbps', v: fmt(wavg(pcStats, 'bitrate_avg')) },
                    { k: 'Render FPS', v: fmt(wavg(pcStats, 'render_fps_avg')) },
                    { k: 'Transmit ms', v: fmt(wavg(pcStats, 'transmit_avg')) },
                  ].map((t) => (
                    <div key={t.k} style={{ border: '1px solid #e4e9f0', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#8a97a6', fontWeight: 600 }}>{t.k}</div>
                      <div style={{ fontSize: 20, fontWeight: 600, fontVariant: 'tabular-nums' }}>{t.v}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['pc', 'scene'] as const).map((v) => (
                      <button key={v} className="refresh-btn" onClick={() => setReportView(v)}
                        style={v === reportView ? { background: '#2B6AC0', color: '#fff' } : undefined}>
                        {v === 'pc' ? 'By PC' : 'By scene'}
                      </button>
                    ))}
                  </div>
                  <select value={metricKey} onChange={(e) => setMetricKey(e.target.value as typeof metricKey)}
                    style={{ padding: '6px 8px', border: '1px solid #d5dce5', borderRadius: 8 }}>
                    {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.unit})</option>)}
                  </select>
                </div>

                <div style={{ display: 'grid', gap: 6, marginBottom: 18 }}>
                  {barRows.map((r) => (
                    <div key={r.name} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 56px', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name}>{r.name}</span>
                      <div style={{ background: '#eef1f5', borderRadius: 4, height: 16 }}>
                        <div style={{ width: `${((r.value ?? 0) / maxVal) * 100}%`, background: '#2B6AC0', height: '100%', borderRadius: 4, minWidth: r.value ? 2 : 0 }} />
                      </div>
                      <span style={{ textAlign: 'right', fontVariant: 'tabular-nums' }}>{r.value == null ? '—' : r.value.toFixed(0)}</span>
                    </div>
                  ))}
                </div>

                <div style={{ overflowX: 'auto' }}>
                  {reportView === 'pc' ? (
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead><tr>{['PC', 'n', 'send', 'peak', 'bitrate', 'tx avg', 'tx max', 'sfps', 'rfps', 'fps min', 'gpu%', '°C'].map((h, i) => (
                        <th key={h} style={thTd(true, i === 0)}>{h}</th>))}</tr></thead>
                      <tbody>
                        {pcStats.map((s) => (
                          <tr key={s.pc_name}>
                            <td style={thTd(false, true)}>{s.pc_name}</td>
                            <td style={thTd()}>{s.samples}</td>
                            <td style={thTd()}>{fmt(s.net_sent_avg)}</td>
                            <td style={thTd()}>{fmt(s.net_sent_max)}</td>
                            <td style={thTd()}>{fmt(s.bitrate_avg)}</td>
                            <td style={thTd()}>{fmt(s.transmit_avg)}</td>
                            <td style={thTd()}>{fmt(s.transmit_max)}</td>
                            <td style={thTd()}>{fmt(s.stream_fps_avg)}</td>
                            <td style={thTd()}>{fmt(s.render_fps_avg)}</td>
                            <td style={thTd()}>{fmt(s.render_fps_min)}</td>
                            <td style={thTd()}>{fmt(s.gpu_avg)}</td>
                            <td style={thTd()}>{fmt(s.gpu_temp_max)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead><tr>{['Scene', 'n', 'PCs', 'send', 'bitrate', 'tx', 'rfps', 'fps min', 'gpu%'].map((h, i) => (
                        <th key={h} style={thTd(true, i === 0)}>{h}</th>))}</tr></thead>
                      <tbody>
                        {sceneStats.map((s) => (
                          <tr key={s.scene_name || 'none'}>
                            <td style={thTd(false, true)}>{s.scene_name || '(no scene)'}</td>
                            <td style={thTd()}>{s.samples}</td>
                            <td style={thTd()}>{s.pcs}</td>
                            <td style={thTd()}>{fmt(s.net_sent_avg)}</td>
                            <td style={thTd()}>{fmt(s.bitrate_avg)}</td>
                            <td style={thTd()}>{fmt(s.transmit_avg)}</td>
                            <td style={thTd()}>{fmt(s.render_fps_avg)}</td>
                            <td style={thTd()}>{fmt(s.render_fps_min)}</td>
                            <td style={thTd()}>{fmt(s.gpu_avg)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 16px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Test Lab</h2>
        <span style={{ background: '#FBEEDA', color: '#7a4310', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Admin · do not run during open hours
        </span>
      </div>
      <p style={{ color: '#54626f', marginTop: 0, fontSize: 14 }}>
        Launch a labeled test run on a set of PCs. The label + exact start/stop window are recorded so the
        telemetry dashboard can compare configs. Starting is blocked while any experience is running in the room.
      </p>

      {runner ? (
        renderActiveSoak()
      ) : activeRun ? (
        <div style={{ ...card, borderColor: '#8CB8EA', background: '#F3F8FE' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#2B6AC0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Test running</div>
          <div style={{ fontSize: 20, fontWeight: 680, margin: '4px 0' }}>{activeRun.label}</div>
          <div style={{ fontSize: 13, color: '#54626f' }}>
            {activeRun.pc_names.length} PC(s) · started {fmtTime(activeRun.started_at)} · {fmtDur(activeRun)}
            {activeRun.config_notes ? ` · ${activeRun.config_notes}` : ''}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0', fontSize: 14 }}>
            <input type="checkbox" checked={killOnStop} onChange={(e) => setKillOnStop(e.target.checked)} />
            Also stop the experiences on these PCs
          </label>
          <button className="refresh-btn" disabled={busy} onClick={doStop}
            style={{ background: '#b3261e', color: '#fff' }}>
            {busy ? 'Stopping…' : 'Stop test run'}
          </button>
        </div>
      ) : (
        <div style={card}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {(['stream', 'soak'] as const).map((m) => (
              <button key={m} className="refresh-btn" onClick={() => setMode(m)}
                style={m === mode ? { background: '#2B6AC0', color: '#fff', fontWeight: 600 } : undefined}>
                {m === 'stream' ? 'Streaming test' : 'Build soak'}
              </button>
            ))}
          </div>
          {mode === 'soak' && (
            <div style={{ fontSize: 13, color: '#54626f', margin: '-6px 0 14px' }}>
              Walks the experience scene-by-scene on each PC (dwell → next → … → complete → restart), watching
              for crashes the whole time. Crashes ring an alarm here and are recorded by the agents
              (crash rows + log bundles). Keep the operator app open on this page while it runs.
            </div>
          )}

          <label style={labelStyle}>Label *</label>
          <input style={input} value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder={mode === 'soak' ? 'e.g. Odyssey-1.3.08-soak' : 'e.g. AV1-90-autoBitrate'} />

          <label style={labelStyle}>Config notes</label>
          <input style={input} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. AV1, 90 Mbps, autoBitrate on, 2880×2880" />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>Experience *</label>
              <select style={input} value={experienceId} onChange={(e) => setExperienceId(e.target.value)}>
                <option value="">Select…</option>
                {experiences.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            {mode === 'stream' ? (
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Version</label>
                <input style={input} value={version} onChange={(e) => setVersion(e.target.value)} placeholder="latest" />
              </div>
            ) : (
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Versions (comma-separated, round-robined)</label>
                <input style={input} value={soakVersions} onChange={(e) => setSoakVersions(e.target.value)}
                  placeholder="e.g. 1.3.08, 1.3.07 — empty = latest installed" />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Language</label>
              <input style={input} value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="default" />
            </div>
          </div>

          {mode === 'soak' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Dwell per scene (s)</label>
                <input style={input} type="number" min={0} value={dwellSec}
                  onChange={(e) => setDwellSec(parseInt(e.target.value || '0', 10))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Final-scene soak (s)</label>
                <input style={input} type="number" min={0} value={finalSoakSec}
                  onChange={(e) => setFinalSoakSec(parseInt(e.target.value || '0', 10))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Cycles per PC (0 = until stopped)</label>
                <input style={input} type="number" min={0} value={cyclesPerPc}
                  onChange={(e) => setCyclesPerPc(parseInt(e.target.value || '0', 10))} />
              </div>
            </div>
          )}

          {mode === 'stream' && (
            <>
              <label style={labelStyle}>Start point</label>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', fontSize: 14, marginBottom: 4 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" name="scenemode" checked={sceneMode === 'full'} onChange={() => setSceneMode('full')} />
                  Full flow (from scene 1)
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" name="scenemode" checked={sceneMode === 'single'} onChange={() => setSceneMode('single')} />
                  Single scene
                </label>
                {sceneMode === 'single' && (
                  <input type="number" min={1} value={sceneNumber}
                    onChange={(e) => setSceneNumber(parseInt(e.target.value || '1', 10))}
                    style={{ width: 72, padding: '6px 8px', border: '1px solid #d5dce5', borderRadius: 8 }} />
                )}
              </div>
              <div style={{ fontSize: 12, color: '#8a97a6', marginBottom: 16 }}>
                Auto tests can't clear scene 0 (it waits for a player to walk in and touch to start), so runs begin at scene 1+
                via the resume-scene arg.
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 8px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>PCs ({selected.size} selected)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="refresh-btn" onClick={() => setSelected(new Set(pcs.map((p) => p.id)))}>All</button>
              <button className="refresh-btn" onClick={() => setSelected(new Set())}>None</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8, marginBottom: 16 }}>
            {pcs.map((pc) => {
              const online = databaseService.isPCOnline(pc.last_heartbeat);
              return (
                <label key={pc.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                  border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 14,
                  opacity: online ? 1 : 0.55, cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={selected.has(pc.id)} onChange={() => toggle(pc.id)} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: online ? '#2F7D53' : '#c0392b' }} />
                  {pc.pc_name || pc.id}
                </label>
              );
            })}
            {pcs.length === 0 && <span style={{ color: '#8a97a6' }}>No PCs found for this room.</span>}
          </div>

          <button className="refresh-btn" disabled={busy} onClick={beginStart}
            style={{ background: '#2B6AC0', color: '#fff', fontWeight: 600 }}>
            {busy ? 'Checking…' : mode === 'soak' ? `Start soak on ${selected.size} PC(s)` : `Start test on ${selected.size} PC(s)`}
          </button>
        </div>
      )}

      {results.length > 0 && (
        <div style={card}>
          <div style={labelStyle}>Launch results</div>
          {results.map((r) => (
            <div key={r.pcName} style={{ display: 'flex', gap: 8, fontSize: 14, padding: '3px 0' }}>
              <span>{r.ok ? '✅' : '❌'}</span><strong>{r.pcName}</strong>
              <span style={{ color: '#54626f' }}>{r.message}</span>
            </div>
          ))}
        </div>
      )}

      <div style={card}>
        <div style={labelStyle}>Recent test runs</div>
        {recent.length === 0 && <span style={{ color: '#8a97a6' }}>None yet.</span>}
        {recent.length > 0 && (
          <div style={{ fontSize: 12, color: '#8a97a6', marginBottom: 4 }}>Click a run to see its results.</div>
        )}
        {recent.map((r) => {
          const isSel = selectedRun?.id === r.id;
          return (
            <div key={r.id} onClick={() => openReport(r)} style={{
              display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '7px 8px',
              borderTop: '1px solid #f0f3f7', cursor: 'pointer', borderRadius: 6,
              background: isSel ? '#EEF4FC' : 'transparent',
            }}>
              <span>{isSel ? '▸ ' : ''}<strong>{r.label}</strong>{r.config_notes ? ` · ${r.config_notes}` : ''}</span>
              <span style={{ color: '#54626f', fontVariant: 'tabular-nums' }}>
                {r.pc_names.length} PC · {fmtTime(r.started_at)}–{fmtTime(r.ended_at)} · {fmtDur(r)}
              </span>
            </div>
          );
        })}
      </div>

      {selectedRun && renderReport()}

      {confirming && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(16,32,46,0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 460, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            {confirming.busyPcs.length > 0 ? (
              // Hard block: a test can never start while anything is running in
              // the room. End/kill those sessions first (Dashboard) if they're
              // stale, then start the test.
              <>
                <h3 style={{ marginTop: 0 }}>Cannot start — the room is in use</h3>
                <div style={{ background: '#FDECEA', border: '1px solid #f5b5ae', color: '#8a1c12', borderRadius: 8, padding: '10px 12px', fontSize: 14, marginBottom: 12 }}>
                  🚫 Experiences are running on: {confirming.busyPcs.join(', ')}
                </div>
                <p style={{ fontSize: 14, color: '#54626f' }}>
                  Tests never interrupt a running room. If these are stale sessions, stop them from the
                  Dashboard first, then start the {mode === 'soak' ? 'soak' : 'test'} again.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button className="refresh-btn" onClick={() => setConfirming(null)}>Close</button>
                  <button className="refresh-btn" disabled={busy} onClick={beginStart}>
                    {busy ? 'Checking…' : 'Re-check'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>{mode === 'soak' ? 'Start soak' : 'Launch test'} on {selectedPcs.length} PC(s)?</h3>
                <p style={{ fontSize: 14, color: '#54626f' }}>
                  {mode === 'soak' ? (
                    <>Label <strong>{label}</strong> · versions <strong>{soakVersionList.join(', ') || 'latest'}</strong> ·
                    dwell {dwellSec}s · final soak {finalSoakSec}s · {cyclesPerPc === 0 ? 'runs until stopped' : `${cyclesPerPc} cycle(s) per PC`} →{' '}
                    {selectedPcs.map((p) => p.pc_name || p.id).join(', ')}. Experiences relaunch continuously until the soak ends.</>
                  ) : (
                    <>Label <strong>{label}</strong> · <strong>{sceneLabel}</strong> → {selectedPcs.map((p) => p.pc_name || p.id).join(', ')}.
                    This launches the experience on each and starts recording the labeled window.</>
                  )}
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button className="refresh-btn" onClick={() => setConfirming(null)}>Cancel</button>
                  <button className="refresh-btn" onClick={mode === 'soak' ? doStartSoak : doStart}
                    style={{ background: '#2B6AC0', color: '#fff', fontWeight: 600 }}>
                    {mode === 'soak' ? 'Start soak' : 'Launch test'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
